/**
 * useIsMobile — viewport-width media query for the mobile layout split.
 *
 * Returns `true` when the viewport is ≤768px. Components consume this
 * hook to branch between the desktop overlapping-fixed layout and the
 * mobile bottom-sheet layout in `routes/Play.tsx`.
 *
 * Returns `false` during SSR / when `matchMedia` is unavailable so the
 * desktop layout renders as the default — degrading to the richer
 * layout on environments without media-query support is the safer fail
 * mode for unknown clients.
 */

import { useEffect, useState } from 'react';

const QUERY = '(max-width: 768px)';

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia(QUERY);
    const handler = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}
